import React, { useEffect, useState, useRef, Children, lazy, Component } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { TreesIcon, FlameIcon, UsersIcon, GlobeIcon, ShieldCheckIcon, ScanLineIcon, SunIcon, BatteryChargingIcon, LeafIcon, DropletIcon, FactoryIcon, HomeIcon, WindIcon, ZapIcon, ThermometerIcon, ScaleIcon, MapPinIcon, BarChart3Icon } from 'lucide-react';
import { Footer } from '../Footer';
import { HowCarbonSafeWorks } from '../HowCarbonSafeWorks';
import type { HowItWorksStep } from '../HowCarbonSafeWorks';
import { PlatformPreview } from '../PlatformPreview';
import type { PlatformPreviewProps } from '../PlatformPreview';
import { MAILTO } from '../../utils/mailto';
const iconMap: Record<string, ComponentType<{
  className?: string;
}>> = {
  TreesIcon,
  FlameIcon,
  UsersIcon,
  GlobeIcon,
  ShieldCheckIcon,
  ScanLineIcon,
  SunIcon,
  BatteryChargingIcon,
  LeafIcon,
  DropletIcon,
  FactoryIcon,
  HomeIcon,
  WindIcon,
  ZapIcon,
  ThermometerIcon,
  ScaleIcon,
  MapPinIcon,
  BarChart3Icon
};
interface StatItem {
  value: string;
  label: string;
  detail: string;
}
interface FeatureItem {
  icon: string;
  title: string;
  description: string;
}
interface FloatingIcon {
  emoji: string;
  position: 'top' | 'right' | 'left';
}
interface DMRVPageProps {
  tagline: string;
  headline: string;
  highlightWord: string;
  description: string;
  badges: string[];
  heroEmoji: string;
  heroImage?: string;
  floatingIcons: FloatingIcon[];
  stats: StatItem[];
  statsHeadline: string;
  statsSubtext: string;
  featuresHeadline: string;
  featuresHighlight: string;
  featuresSubtext: string;
  features: FeatureItem[];
  ctaHeadline: string;
  ctaHighlight: string;
  ctaSubtext: string;
  ctaButtonText: string;
  howItWorksVerticalName?: string;
  howItWorksSubtext?: string;
  howItWorksSteps?: HowItWorksStep[];
  impactImageUrl?: string;
  impactImageAlt?: string;
  impactTagline?: string;
  impactHeadline?: string;
  impactDescription?: string;
  impactCtaText?: string;
  platformPreview?: PlatformPreviewProps;
}
/* ─── Hexagon Visual ─── */
function HexagonVisual({
  emoji,
  heroImage,
  floatingIcons,
  id
}: {
  emoji: string;
  heroImage?: string;
  floatingIcons: FloatingIcon[];
  id: string;
}) {
  const positions = {
    top: 'top-6 left-1/2 -translate-x-1/2',
    right: 'bottom-16 right-4',
    left: 'bottom-8 left-8'
  };
  const animations = {
    top: {
      y: [0, -6, 0]
    },
    right: {
      y: [0, 6, 0]
    },
    left: {
      y: [0, -5, 0]
    }
  };
  const delays = {
    top: 0,
    right: 0.5,
    left: 1
  };
  return <div className="relative w-full max-w-lg mx-auto aspect-square" data-id="element-370">
      <svg className="absolute inset-0 w-full h-full opacity-[0.07]" aria-hidden="true" data-id="element-371">

        <defs data-id="element-372">
          <pattern id={`${id}-grid`} width="40" height="40" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-373">

            <line x1="0" y1="0" x2="0" y2="40" stroke="#1B4332" strokeWidth="0.5" data-id="element-374" />

            <line x1="0" y1="0" x2="40" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-375" />

          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${id}-grid)`} data-id="element-376" />
      </svg>

      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400" aria-hidden="true" data-id="element-377">

        <defs data-id="element-378">
          <radialGradient id={`${id}-glow`} cx="50%" cy="50%" r="50%" data-id="element-379">
            <stop offset="0%" stopColor="#52B788" stopOpacity="0.15" data-id="element-380" />
            <stop offset="100%" stopColor="#52B788" stopOpacity="0" data-id="element-381" />
          </radialGradient>
        </defs>
        <circle cx="200" cy="200" r="180" fill={`url(#${id}-glow)`} data-id="element-382" />
        <polygon points="200,30 355,110 355,290 200,370 45,290 45,110" fill="none" stroke="#52B788" strokeWidth="1.5" opacity="0.3" data-id="element-383" />

        <polygon points="200,70 325,135 325,265 200,330 75,265 75,135" fill="none" stroke="#52B788" strokeWidth="1" opacity="0.2" data-id="element-384" />

        <polygon points="200,110 295,160 295,240 200,290 105,240 105,160" fill="#52B788" fillOpacity="0.08" stroke="#52B788" strokeWidth="2" opacity="0.5" data-id="element-385" />

        <circle cx="200" cy="30" r="5" fill="#52B788" opacity="0.6" data-id="element-386" />
        <circle cx="355" cy="110" r="4" fill="#9CA3AF" opacity="0.5" data-id="element-387" />
        <circle cx="355" cy="290" r="5" fill="#52B788" opacity="0.6" data-id="element-388" />
        <circle cx="200" cy="370" r="4" fill="#9CA3AF" opacity="0.5" data-id="element-389" />
        <circle cx="45" cy="290" r="5" fill="#52B788" opacity="0.6" data-id="element-390" />
        <circle cx="45" cy="110" r="4" fill="#9CA3AF" opacity="0.5" data-id="element-391" />
        <circle cx="278" cy="70" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-392" />
        <circle cx="355" cy="200" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-393" />
        <circle cx="278" cy="330" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-394" />
        <circle cx="122" cy="330" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-395" />
        <circle cx="45" cy="200" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-396" />
        <circle cx="122" cy="70" r="3" fill="#9CA3AF" opacity="0.4" data-id="element-397" />
      </svg>

      <div className="absolute inset-0 flex items-center justify-center" data-id="element-398">
        {heroImage ? <img src={heroImage} alt="icon" className="w-24 h-24 object-contain" data-id="element-399" /> : <div className="text-7xl select-none" role="img" aria-label="icon" data-id="element-400">
            {emoji}
          </div>}
      </div>

      {floatingIcons.map((fi, i) => <motion.div key={i} className={`absolute ${positions[fi.position]} ${fi.position === 'right' ? 'w-12 h-12' : 'w-11 h-11'} rounded-full ${fi.position === 'right' ? 'bg-carbon-deep' : 'bg-carbon-bright'} flex items-center justify-center shadow-lg ${fi.position !== 'right' ? 'shadow-carbon-bright/30' : ''}`} animate={animations[fi.position]} transition={{
      duration: 3 + i * 0.5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: delays[fi.position]
    }} data-id="element-401">

          <span className="text-white text-lg" data-id="element-402">{fi.emoji}</span>
        </motion.div>)}
    </div>;
}
/* ─── Hero ─── */
function DMRVHero({
  tagline,
  headline,
  highlightWord,
  description,
  badges,
  heroEmoji,
  heroImage,
  floatingIcons,
  id
}: DMRVPageProps & {
  id: string;
}) {
  const parts = headline.split(highlightWord);
  return <section className="relative w-full overflow-hidden bg-white" data-id="element-403">
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-404">

        <defs data-id="element-405">
          <pattern id={`${id}-hero-grid`} width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-406">

            <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-407" />

            <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-408" />

          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${id}-hero-grid)`} data-id="element-409" />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36" data-id="element-410">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-411">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-412">

            <p className="text-sm font-semibold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-413">
              {tagline}
            </p>
            <h1 className="text-[3.5rem] font-bold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-414">
              {parts[0]}
              <span className="relative inline-block" data-id="element-415">
                <span className="relative z-10" data-id="element-416">{highlightWord}</span>
                <span className="absolute bottom-1 left-0 right-0 h-[40%] bg-carbon-bright/25 -z-0 rounded-sm" aria-hidden="true" data-id="element-417" />

              </span>
              {parts[1] || ''}
            </h1>
            <p className="mt-6 text-lg text-carbon-deep leading-relaxed max-w-xl" data-id="element-418">
              {description}
            </p>
            <div className="mt-5 flex flex-wrap gap-3" data-id="element-419">
              {badges.map(badge => <span key={badge} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#9aa6af] bg-white text-sm font-medium text-carbon-deep" data-id="element-420">

                  <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-421" />
                  {badge}
                </span>)}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4" data-id="element-422">
              <a href={MAILTO.demo} className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#6bcb78] text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-423">

                See CarbonSafe in action
                <ArrowRightIcon className="w-4 h-4" data-id="element-424" />
              </a>
              <a href={MAILTO.platformAccess} className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#2d3c48] text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-425">

                Explore The Platform
              </a>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-426">

            <HexagonVisual emoji={heroEmoji} heroImage={heroImage} floatingIcons={floatingIcons} id={id} data-id="element-427" />

          </motion.div>
        </div>
      </div>
    </section>;
}
/* ─── Animated Stat ─── */
function AnimatedStat({
  value,
  label,
  detail
}: StatItem) {
  const numericMatch = value.match(/^([\d.]+)(.*)$/);
  const numericValue = numericMatch ? parseFloat(numericMatch[1]) : 0;
  const suffix = numericMatch ? numericMatch[2] : value;
  const isNumeric = !!numericMatch;
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, v => {
    if (numericValue < 10) return Number(v.toFixed(1));
    return Math.round(v);
  });
  useEffect(() => {
    if (isInView && isNumeric) {
      const controls = animate(mv, numericValue, {
        duration: 2,
        ease: [0.22, 1, 0.36, 1]
      });
      return controls.stop;
    }
  }, [isInView, numericValue, mv, isNumeric]);
  useEffect(() => {
    const unsub = rounded.on('change', v => setDisplay(v));
    return unsub;
  }, [rounded]);
  return <div ref={ref} className="text-center" data-id="element-428">
      <p className="text-4xl sm:text-5xl font-semibold text-carbon-bright" data-id="element-429">
        {isNumeric ? `${display.toLocaleString()}${suffix}` : value}
      </p>
      <p className="mt-2 text-base font-bold text-carbon-deep" data-id="element-430">{label}</p>
      <p className="mt-1 text-sm text-carbon-deep leading-relaxed" data-id="element-431">{detail}</p>
    </div>;
}
/* ─── Stats ─── */
function DMRVStats({
  stats,
  statsHeadline,
  statsSubtext
}: Pick<DMRVPageProps, 'stats' | 'statsHeadline' | 'statsSubtext'>) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section ref={ref} className="w-full bg-carbon-deep py-20 sm:py-24" data-id="element-432">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-433">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-434">

          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-435">
            {statsHeadline}
          </h2>
          <p className="mt-3 text-lg text-white/60 max-w-2xl mx-auto" data-id="element-436">
            {statsSubtext}
          </p>
        </motion.div>
        <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.12
          }
        }
      }} data-id="element-437">

          {stats.map(stat => <motion.div key={stat.label} variants={{
          hidden: {
            opacity: 0,
            y: 30
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6
            }
          }
        }} data-id="element-438">

              <AnimatedStat {...stat} data-id="element-439" />
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
/* ─── Features ─── */
function DMRVFeatures({
  featuresHeadline,
  featuresHighlight,
  featuresSubtext,
  features
}: Pick<DMRVPageProps, 'featuresHeadline' | 'featuresHighlight' | 'featuresSubtext' | 'features'>) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28 border-t border-b border-carbon-geo" data-id="element-440">

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-441">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-442">

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-carbon-deep tracking-tight" data-id="element-443">
            {featuresHeadline}{' '}
            <span data-id="element-444">{featuresHighlight}</span>
          </h2>
          <p className="mt-4 text-lg text-carbon-deep max-w-2xl mx-auto" data-id="element-445">
            {featuresSubtext}
          </p>
        </motion.div>
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} data-id="element-446">

          {features.map(feature => {
          const IconComponent = iconMap[feature.icon];
          return <motion.article key={feature.title} className="p-7 rounded-2xl border border-[#9aa6af] bg-white" variants={cardVariants} data-id="element-447">

                <div className="w-14 h-14 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-5" data-id="element-448">
                  {IconComponent && <IconComponent className="w-7 h-7 text-carbon-bright" data-id="element-449" />}
                </div>
                <h3 className="text-lg font-bold text-carbon-deep mb-2" data-id="element-450">
                  {feature.title}
                </h3>
                <p className="text-sm text-carbon-deep leading-relaxed" data-id="element-451">
                  {feature.description}
                </p>
              </motion.article>;
        })}
        </motion.div>
      </div>
    </section>;
}
/* ─── CTA ─── */
function DMRVCTA({
  ctaHeadline,
  ctaHighlight,
  ctaSubtext,
  ctaButtonText
}: Pick<DMRVPageProps, 'ctaHeadline' | 'ctaHighlight' | 'ctaSubtext' | 'ctaButtonText'>) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section ref={ref} className="w-full py-20 sm:py-28 bg-white" data-id="element-452">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-453">
        <motion.div className="relative overflow-hidden rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center" style={{
        background: 'linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)'
      }} initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-454">

          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #52B788, transparent)'
        }} aria-hidden="true" data-id="element-455" />

          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #6BCB77, transparent)'
        }} aria-hidden="true" data-id="element-456" />

          <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-457">
            {ctaHeadline}
            <br data-id="element-458" />
            <span data-id="element-459">{ctaHighlight}</span>
          </h2>
          <p className="relative mt-5 text-lg text-white/70 max-w-xl mx-auto" data-id="element-460">
            {ctaSubtext}
          </p>
          <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-461">
            <a href={MAILTO.demo} className="inline-flex items-center gap-2 px-8 py-4 bg-[#6bcb78] text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-black/20" data-id="element-462">

              {ctaButtonText}
              <ArrowRightIcon className="w-4 h-4" data-id="element-463" />
            </a>
        </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── Impact Image Section ─── */
function DMRVImpactSection({
  impactImageUrl,
  impactImageAlt,
  impactTagline,
  impactHeadline,
  impactDescription,
  impactCtaText
}: Pick<DMRVPageProps, 'impactImageUrl' | 'impactImageAlt' | 'impactTagline' | 'impactHeadline' | 'impactDescription' | 'impactCtaText'>) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  if (!impactImageUrl || !impactHeadline) return null;
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28" data-id="element-464">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-465">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-466">
          {/* Image */}
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative" data-id="element-467">

            <div className="rounded-2xl overflow-hidden shadow-xl shadow-carbon-deep/8 border border-[#9aa6af]" data-id="element-468">
              <img src={impactImageUrl} alt={impactImageAlt || ''} className="w-full h-auto object-cover" loading="lazy" data-id="element-469" />

            </div>
            <div className="absolute -bottom-3 -right-3 w-24 h-24 rounded-2xl bg-carbon-bright/10 -z-10" aria-hidden="true" data-id="element-470" />

          </motion.div>

          {/* Text */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-471">

            {impactTagline && <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-472">
                {impactTagline}
              </span>}
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-deep tracking-tight leading-tight" data-id="element-473">
              {impactHeadline}
            </h2>
            {impactDescription && <p className="mt-5 text-lg text-carbon-deep leading-relaxed" data-id="element-474">
                {impactDescription}
              </p>}
            {impactCtaText && <div className="mt-8" data-id="element-475">
                <a href={MAILTO.demo} className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#6bcb78] text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-476">

                  {impactCtaText}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-477" />
                </a>
              </div>}
          </motion.div>
        </div>
      </div>
    </section>;
}
/* ─── Page Template ─── */
export function DMRVPageTemplate(props: DMRVPageProps & {
  id: string;
}) {
  return <>
      <DMRVHero {...props} data-id="element-478" />
      <DMRVStats stats={props.stats} statsHeadline={props.statsHeadline} statsSubtext={props.statsSubtext} data-id="element-479" />

      {props.howItWorksSteps && props.howItWorksVerticalName && props.howItWorksSubtext && <HowCarbonSafeWorks verticalName={props.howItWorksVerticalName} subtext={props.howItWorksSubtext} steps={props.howItWorksSteps} data-id="element-480" />}
      <DMRVImpactSection impactImageUrl={props.impactImageUrl} impactImageAlt={props.impactImageAlt} impactTagline={props.impactTagline} impactHeadline={props.impactHeadline} impactDescription={props.impactDescription} impactCtaText={props.impactCtaText} data-id="element-481" />

      <DMRVFeatures featuresHeadline={props.featuresHeadline} featuresHighlight={props.featuresHighlight} featuresSubtext={props.featuresSubtext} features={props.features} data-id="element-482" />

      {props.platformPreview && <PlatformPreview {...props.platformPreview} data-id="element-483" />}
      <DMRVCTA ctaHeadline={props.ctaHeadline} ctaHighlight={props.ctaHighlight} ctaSubtext={props.ctaSubtext} ctaButtonText={props.ctaButtonText} data-id="element-484" />

      <Footer data-id="element-485" />
    </>;
}